/**
 * ReadingDocument → DOM (spec §9, §11).
 * Escapes all text; markdown is a tiny fact-outline subset (bullets + bold + italics).
 */

/**
 * @param {HTMLElement} container
 * @param {object} reading - ReadingDocument
 * @param {{ label?: string }} [options]
 */
export function renderReadingDocument(container, reading, options = {}) {
  if (!container) return;
  container.replaceChildren();

  const header = document.createElement("div");
  header.className = "dynamic-reading-header";

  const label = document.createElement("p");
  label.className = "dynamic-reading-label";
  const name = reading.displayName || "this chart";
  const defaultLabel =
    reading.source === "llm"
      ? `AI reading for ${name} (OpenRouter · Grok · Phase 2b)`
      : reading.source === "outline"
        ? `Fact outline for ${name} (Phase 1 — not a full essay)`
        : `Reading for ${name}`;

  label.textContent = options.label || defaultLabel;
  header.appendChild(label);


  const meta = document.createElement("p");
  meta.className = "dynamic-reading-meta";
  meta.textContent = `source: ${reading.source} · hash ${reading.payloadHash}`;
  header.appendChild(meta);

  container.appendChild(header);

  for (const section of reading.sections || []) {
    const article = document.createElement("article");
    article.className = "reading-block outline-block";
    if (section.skipped) article.classList.add("is-skipped");
    article.dataset.sectionId = section.id;

    // UX (§22): hide the esoteric section titles. Only the "Synthesis" title is
    // kept as a header; each section's opening phrase is bolded instead, so the
    // reading reads as continuous, grounded prose.
    const isSynthesis = section.id === "synthesis";
    if (isSynthesis) article.classList.add("is-synthesis");
    if (isSynthesis) {
      const h3 = document.createElement("h3");
      h3.className = "outline-section-title";
      h3.appendChild(document.createTextNode(section.title || "Synthesis & Conscious Choice"));
      article.appendChild(h3);
    }

    const body = document.createElement("div");
    body.className = "outline-body";
    body.appendChild(
      markdownToFragment(section.markdown || "", { boldFirstBlock: true })
    );
    article.appendChild(body);

    container.appendChild(article);
  }
}

/**
 * Minimal safe markdown: paragraphs, `- ` lists, **bold**, *italic*, _italic_.
 *
 * Options:
 * - `hideHeadings` (LLM essay path): drop any `#`/`##`/`###` heading from the
 *   rendered output EXCEPT a "Synthesis" heading (kept as-is). Each hidden
 *   section's opening paragraph gets its first few words bolded, so the
 *   technical/archetype titles are replaced by readable opening phrases.
 * - `boldFirstBlock`: accepted for backward compatibility (no-op — every
 *   paragraph's leading words are bolded now).
 * @param {string} md
 * @param {{ hideHeadings?: boolean, boldFirstBlock?: boolean }} [options]
 * @returns {DocumentFragment}
 */
export function markdownToFragment(md, options = {}) {
  const frag = document.createDocumentFragment();
  const lines = String(md).replace(/\r\n/g, "\n").split("\n");
  /** @type {HTMLUListElement | null} */
  let list = null;

  const flushList = () => {
    if (list) {
      frag.appendChild(list);
      list = null;
    }
  };

  for (const raw of lines) {
    const line = stripBlockquote(raw.trimEnd());
    const heading = line.match(/^(#{1,4})\s+(.*)$/);
    if (heading) {
      flushList();
      const level = heading[1].length;
      const text = heading[2].trim();
      const isSynthesis = /^(synthesis|core overview)\b/i.test(text);
      if (options.hideHeadings && !isSynthesis) {
        // Drop the technical title; the paragraphs' bolded lead phrases stand in.
        continue;
      }
      const el = document.createElement(
        level === 1 ? "h3" : level === 2 ? "h3" : level === 3 ? "h4" : "h5"
      );
      appendInline(el, text);
      frag.appendChild(el);
      continue;
    }
    const bullet = line.match(/^\s*-\s+(.*)$/);
    if (bullet) {
      if (!list) {
        list = document.createElement("ul");
        list.className = "outline-list";
      }
      const li = document.createElement("li");
      appendInline(li, bullet[1]);
      list.appendChild(li);
      continue;
    }
    flushList();
    if (!line.trim()) continue;
    const p = document.createElement("p");
    // Bold the first 4 words of EVERY paragraph — lead-phrase anchors that
    // break up dense prose (§22 UX extended to all paragraphs).
    const paragraphText = boldLeadingWords(line.trim(), 4);
    appendInline(p, paragraphText);
    frag.appendChild(p);
  }
  flushList();
  return frag;
}

/**
 * LLM summaries sometimes arrive wrapped as a Markdown blockquote (`> text`).
 * The reading is prose, not a quotation, so drop the marker — including bare
 * `>` separator lines, which then render as nothing.
 * @param {string} line
 * @returns {string}
 */
function stripBlockquote(line) {
  return line.replace(/^\s*(?:>\s?)+/, "");
}

/**
 * Wrap the first `n` plain words of a paragraph in **bold** markers, unless the
 * paragraph is already bolded up front. This turns a section's opening phrase
 * (e.g. "Your Ascendant in Taurus") into its visual marker once the technical
 * section title is suppressed.
 * @param {string} text
 * @param {number} n
 * @returns {string}
 */
function boldLeadingWords(text, n) {
  const trimmed = text.trim();
  // Leave paragraphs that already open with emphasis (bold ** or italic * / _)
  // alone — wrapping them would corrupt the inline markers.
  if (!trimmed || /^(\*\*|\*|_)/.test(trimmed)) return trimmed;
  let cursor = 0;
  let count = 0;
  while (count < n) {
    const space = trimmed.indexOf(" ", cursor);
    if (space === -1) break;
    cursor = space + 1;
    count++;
  }
  if (cursor === 0) return trimmed;
  const lead = trimmed.slice(0, cursor).trimEnd();
  const rest = trimmed.slice(cursor).trimStart();
  if (!lead) return trimmed;
  return `**${lead}**${rest ? " " + rest : ""}`;
}

/**
 * @param {HTMLElement} el
 * @param {string} text
 */
function appendInline(el, text) {
  // Split on **bold**, *italic*, and _italic_ (non-greedy, no nesting)
  const re = /(\*\*[^*]+\*\*|\*[^*]+\*|_[^_]+_)/g;
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) {
      el.appendChild(document.createTextNode(text.slice(last, m.index)));
    }
    const token = m[0];
    if (token.startsWith("**")) {
      const strong = document.createElement("strong");
      strong.textContent = token.slice(2, -2);
      el.appendChild(strong);
    } else {
      const em = document.createElement("em");
      em.textContent = token.slice(1, -1);
      el.appendChild(em);
    }
    last = m.index + token.length;
  }
  if (last < text.length) {
    el.appendChild(document.createTextNode(text.slice(last)));
  }
}

/**
 * Show sample essay; hide dynamic outline.
 * @param {HTMLElement | null} sampleEl
 * @param {HTMLElement | null} dynamicEl
 */
export function showSampleReading(sampleEl, dynamicEl) {
  if (sampleEl) sampleEl.hidden = false;
  if (dynamicEl) {
    dynamicEl.hidden = true;
    dynamicEl.replaceChildren();
  }
}

/**
 * Progressive re-render of a streamed markdown string into a container.
 * Called on each delta while the LLM response streams in.
 *
 * The LLM emits one continuous Markdown document with `## Section` headings.
 * To match the static sample reading's format exactly: hide the technical
 * headings (keep only a "Synthesis" heading), wrap each section in an
 * `<article class="reading-block">` so the horizontal divider line and padding
 * appear between sections, and bold the first 4 words of every paragraph as
 * readable anchors that break up dense prose (§22 UX).
 * @param {HTMLElement} container
 * @param {string} md
 */
export function renderStreamingText(container, md) {
  if (!container) return;
  container.replaceChildren(renderReadingSections(md || ""));
}

/**
 * Render a raw LLM Markdown reading as section-articles. Splits the document on
 * `#`/`##`/`###` headings so each section becomes its own
 * `.reading-block` with a divider line, matching the static sample format.
 * Every technical heading is hidden except "Synthesis" (kept as an h3), and
 * every paragraph gets its first 4 words bolded.
 * @param {string} md
 * @returns {DocumentFragment}
 */
function renderReadingSections(md) {
  const frag = document.createDocumentFragment();
  const lines = String(md).replace(/\r\n/g, "\n").split("\n");
  /** @type {HTMLDivElement | null} */
  let currentList = null;
  /** @type {HTMLArticleElement | null} */
  let current = null;
  /** @type {HTMLDivElement | null} */
  let body = null;

  const flushArticle = () => {
    if (current) frag.appendChild(current);
    current = null;
    body = null;
  };
  const flushList = () => {
    if (currentList && body) {
      body.appendChild(currentList);
      currentList = null;
    }
  };
  const ensureBody = () => {
    if (!body) {
      body = document.createElement("div");
      body.className = "outline-body";
      current.appendChild(body);
    }
  };

  for (const raw of lines) {
    const line = stripBlockquote(raw.trimEnd());
    const heading = line.match(/^(#{1,4})\s+(.*)$/);
    if (heading) {
      flushList();
      // "Core Overview & Conscious Choice" (formerly "Synthesis & Conscious
      // Choice") keeps its highlighted summary card.
      const isSynthesis =
        /\bsynthesis\b/i.test(heading[2]) || /\bcore overview\b/i.test(heading[2]);
      flushArticle();
      const art = document.createElement("article");
      art.className = "reading-block outline-block";
      const level = heading[1].length;
      if (isSynthesis) {
        art.classList.add("is-synthesis");
        const h3 = document.createElement("h3");
        h3.className = "outline-section-title";
        h3.appendChild(
          document.createTextNode(normalizeSynthesisTitle(heading[2]))
        );
        art.appendChild(h3);
      } else if (level <= 2) {
        // Top-level part headings ("The Engines of Your Life") become visible
        // part titles.
        art.classList.add("is-part");
        const h3 = document.createElement("h3");
        h3.className = "outline-section-title part-title";
        h3.appendChild(document.createTextNode(heading[2].trim()));
        art.appendChild(h3);
      } else {
        // ### engine/crucible subheadings become visible h4 subheads,
        // kept inside the active part article.
        const h4 = document.createElement("h4");
        h4.className = "reading-subhead";
        h4.appendChild(document.createTextNode(heading[2].trim()));
        art.appendChild(h4);
      }
      current = art;
      body = document.createElement("div");
      body.className = "outline-body";
      current.appendChild(body);
      continue;
    }
    const bullet = line.match(/^\s*-\s+(.*)$/);
    if (bullet) {
      ensureBody();
      if (!currentList) {
        currentList = document.createElement("ul");
        currentList.className = "outline-list";
      }
      const li = document.createElement("li");
      appendInline(li, bullet[1]);
      currentList.appendChild(li);
      continue;
    }
    flushList();
    if (!line.trim()) continue;
    ensureBody();
    const p = document.createElement("p");
    // Bold the first 4 words of EVERY paragraph — lead-phrase anchors that
    // break up dense prose (§22 UX extended to all paragraphs).
    const paragraphText = boldLeadingWords(line.trim(), 4);
    appendInline(p, paragraphText);
    body.appendChild(p);
  }
  flushList();
  flushArticle();
  return frag;
}

/** Normalize a Synthesis section heading to a clean "Synthesis & Conscious Choice" title. */
function normalizeSynthesisTitle(text) {
  const t = String(text).trim();
  const cleaned = t.replace(/^movement\s*\d+\s*[-–—]\s*/i, "").trim();
  return cleaned || "Synthesis & Conscious Choice";
}

/**
 * Hide sample essay; show dynamic container (caller fills it).
 * @param {HTMLElement | null} sampleEl
 * @param {HTMLElement | null} dynamicEl
 */
export function showDynamicReading(sampleEl, dynamicEl) {
  if (sampleEl) sampleEl.hidden = true;
  if (dynamicEl) dynamicEl.hidden = false;
}
